import React, { useState } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { useAPIKeys } from '../contexts/APIKeyContext';
import GradingAgent from '../GradingAgent';
import InputFormatSelector from './InputFormatSelector';
import NaturalLanguageInput from './NaturalLanguageInput';
import VisualInput from './VisualInput';
import APIKeySetup from './APIKeySetup';
import { exportToJSON, exportToCSV, exportToPDF } from '../utils/exportUtils';

const GradingInterface = () => {
  const { settings } = useSettings();
  const { hasConfiguredProvider, getApiKeysForGrading, getPreferredProvider } = useAPIKeys();
  const [answerKey, setAnswerKey] = useState('');
  const [studentWork, setStudentWork] = useState('');
  const [results, setResults] = useState([]);
  const [isGrading, setIsGrading] = useState(false);
  const [progress, setProgress] = useState({ status: 'idle', message: '' });
  const [totalScore, setTotalScore] = useState(0);
  const [maxPossibleScore, setMaxPossibleScore] = useState(0);
  const [inputFormat, setInputFormat] = useState('natural');
  const [showApiSetup, setShowApiSetup] = useState(false);
  const [expandedResults, setExpandedResults] = useState(new Set());

  // Example templates
  const exampleAnswerKey = `{
  "questions": [
    {
      "questionNumber": "1",
      "question": "Explain the concept of photosynthesis.",
      "correctAnswer": "Photosynthesis is the process by which plants convert light energy into chemical energy, producing oxygen and glucose from carbon dioxide and water.",
      "maxScore": 10,
      "rubric": {
        "accuracy": 40,
        "completeness": 30,
        "clarity": 20,
        "originality": 10
      }
    },
    {
      "questionNumber": "2",
      "question": "What are the main stages of cellular respiration?",
      "correctAnswer": "The main stages are glycolysis, the Krebs cycle, and the electron transport chain.",
      "maxScore": 8,
      "rubric": {
        "accuracy": 50,
        "completeness": 30,
        "clarity": 20
      }
    }
  ]
}`;

  const exampleStudentWork = `{
  "studentName": "John Doe",
  "answers": [
    {
      "questionNumber": "1",
      "answer": "Photosynthesis is when plants use sunlight to make food. They take in carbon dioxide and release oxygen."
    },
    {
      "questionNumber": "2",
      "answer": "Cellular respiration has glycolysis, Krebs cycle, and something with electrons."
    }
  ]
}`;

  const loadExample = () => {
    if (inputFormat === 'json') {
      setAnswerKey(exampleAnswerKey);
      setStudentWork(exampleStudentWork);
    }
  };

  const clearAll = () => {
    setAnswerKey('');
    setStudentWork('');
    setResults([]);
    setProgress({ status: 'idle', message: '' });
    setTotalScore(0);
    setMaxPossibleScore(0);
    setExpandedResults(new Set());
  };

  const validateInputs = () => {
    if (!answerKey.trim()) {
      alert('Please provide an answer key.');
      return false;
    }
    if (!studentWork.trim()) {
      alert('Please provide student work to grade.');
      return false;
    }

    if (inputFormat === 'json') {
      try {
        JSON.parse(answerKey);
        JSON.parse(studentWork);
        return true;
      } catch (error) {
        alert(`Invalid JSON format: ${error.message}`);
        return false;
      }
    }

    return true;
  };

  const startGrading = async () => {
    if (!hasConfiguredProvider()) {
      setShowApiSetup(true);
      return;
    }

    if (!validateInputs()) return;

    setIsGrading(true);
    setProgress({ status: 'starting', message: 'Initializing grading process...' });

    try {
      let parsedAnswerKey, parsedStudentWork;

      if (inputFormat === 'json') {
        parsedAnswerKey = JSON.parse(answerKey);
        parsedStudentWork = JSON.parse(studentWork);
      } else {
        try {
          parsedAnswerKey = JSON.parse(answerKey);
          parsedStudentWork = JSON.parse(studentWork);
        } catch (error) {
          throw new Error(`Invalid data format: ${error.message}. Please ensure inputs are properly converted.`);
        }
      }

      // Scrub PII from student work before grading
      const { scrubPIIFromObject } = await import('../utils/piiScrubber.js');
      const scrubbedResult = scrubPIIFromObject(parsedStudentWork, {
        scrubNames: true,
        scrubEmails: true,
        scrubPhones: true,
        scrubSSNs: true,
        scrubCreditCards: true,
        scrubIPs: true,
        scrubDates: false,
        scrubURLs: false,
        preserveContext: true
      });
      
      const apiKeys = getApiKeysForGrading();
      const preferredProvider = getPreferredProvider() || settings.generalSettings.defaultLLM;

      const agent = new GradingAgent(
        parsedAnswerKey,
        scrubbedResult.scrubbedObject, // Use scrubbed student work
        settings.similaritySettings.threshold,
        preferredProvider,
        {
          onProgress: (update) => {
            setProgress(update);
          },
          enableCache: true,
          timeout: 30000,
          apiKeys: apiKeys
        }
      );

      const gradingResults = await agent.compareAnswers();
      
      // Restore original student answers in results (they were scrubbed for LLM calls)
      gradingResults.forEach((result, index) => {
        // Find original answer from parsedStudentWork
        if (parsedStudentWork.answers && parsedStudentWork.answers[index]) {
          result.studentAnswer = parsedStudentWork.answers[index].answer || result.studentAnswer;
        }
      });
      
      const total = gradingResults.reduce((sum, r) => sum + r.value, 0);
      const maxTotal = gradingResults.reduce((sum, r) => sum + r.maxScore, 0);
      
      setResults(gradingResults);
      setTotalScore(total);
      setMaxPossibleScore(maxTotal);
      setProgress({ status: 'complete', message: 'Grading completed successfully!' });
      
    } catch (error) {
      console.error('Grading error:', error);
      setProgress({ 
        status: 'error', 
        message: `Grading failed: ${error.message}. Please check your API keys and try again.` 
      });
    } finally {
      setIsGrading(false);
    }
  };

  const toggleResultExpansion = (index) => {
    const newExpanded = new Set(expandedResults);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedResults(newExpanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'starting': return 'text-blue-600 dark:text-blue-400';
      case 'processing': return 'text-purple-600 dark:text-purple-400';
      case 'complete': return 'text-green-600 dark:text-green-400';
      case 'error': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getSimilarityColor = (score) => {
    if (score === 'High') return 'text-green-600 dark:text-green-400';
    if (score === 'Medium') return 'text-yellow-600 dark:text-yellow-400';
    if (score === 'Low') return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const renderInputSection = () => {
    switch (inputFormat) {
      case 'natural':
        return (
          <NaturalLanguageInput
            onAnswerKeyChange={setAnswerKey}
            onStudentWorkChange={setStudentWork}
          />
        );
      case 'visual':
        return (
          <VisualInput
            onAnswerKeyChange={setAnswerKey}
            onStudentWorkChange={setStudentWork}
          />
        );
      case 'json':
      default:
        return (
          <div className="space-y-8">
            <div className="premium-card p-6 animate-slide-up">
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 tracking-wide">
                Answer Key
              </label>
              <textarea
                value={answerKey}
                onChange={(e) => setAnswerKey(e.target.value)}
                placeholder="Paste your answer key in JSON format..."
                className="premium-input w-full h-72 p-5 border rounded-xl bg-white dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 font-mono text-sm resize-none shadow-inner"
                spellCheck="false"
              />
            </div>

            <div className="premium-card p-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 tracking-wide">
                Student Work
              </label>
              <textarea
                value={studentWork}
                onChange={(e) => setStudentWork(e.target.value)}
                placeholder="Paste student work in JSON format..."
                className="premium-input w-full h-72 p-5 border rounded-xl bg-white dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 font-mono text-sm resize-none shadow-inner"
                spellCheck="false"
              />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* API Key Setup Modal */}
      {showApiSetup && (
        <APIKeySetup onClose={() => setShowApiSetup(false)} isModal={true} />
      )}

      {/* Main Content - Centered with Max Width */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        {/* Premium Header */}
        <div className="text-center mb-16 animate-fade-in pt-2">
          <h1 className="text-6xl font-bold mb-4 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent tracking-tight leading-[1.15] py-2 inline-block">
            AI Grading
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 font-light tracking-wide">
            Grade written assignments with AI-powered analysis
          </p>
          <div className="mt-6 flex items-center justify-center gap-2">
            <div className="h-px w-16 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
            <div className="w-2 h-2 rounded-full bg-primary"></div>
            <div className="h-px w-16 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
          </div>
        </div>

        {/* API Setup Banner - Premium */}
        {!hasConfiguredProvider() && (
          <div className="mb-10 premium-card p-5 animate-slide-up border-amber-200/60 dark:border-amber-800/40 bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-900/10 dark:to-orange-900/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <span className="text-amber-600 dark:text-amber-400 text-lg">🔑</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                    Configure AI provider
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                    Set up your API key to enable AI grading
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowApiSetup(true)}
                className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
              >
                Setup
              </button>
            </div>
          </div>
        )}

        {/* Input Format Tabs */}
        <InputFormatSelector 
          onFormatChange={setInputFormat} 
          activeFormat={inputFormat} 
        />

        {/* Input Section */}
        <div className="mb-12 animate-slide-up">
          {renderInputSection()}
        </div>

        {/* Premium Action Buttons */}
        <div className="flex items-center justify-center gap-4 mb-10">
          {inputFormat === 'json' && (
            <button
              onClick={loadExample}
              className="premium-button px-6 py-3 bg-gradient-to-r from-primary via-purple-600 to-primary bg-[length:200%_auto] hover:bg-[length:100%_auto] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl text-sm tracking-wide"
            >
              Load Example
            </button>
          )}
          <button
            onClick={clearAll}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl"
          >
            Clear
          </button>
          <button
            onClick={startGrading}
            disabled={isGrading}
            className="premium-button px-10 py-4 bg-gradient-to-r from-primary via-purple-600 to-primary bg-[length:200%_auto] hover:bg-[length:100%_auto] text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg shadow-xl hover:shadow-2xl text-base tracking-wide"
          >
            {isGrading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Grading...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span>Grade</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            )}
          </button>
        </div>

        {/* Premium Progress Indicator */}
        {progress.message && progress.status !== 'idle' && (
          <div className="mb-10 text-center animate-fade-in">
            <div className={`inline-flex items-center gap-3 px-6 py-3 rounded-xl backdrop-blur-sm ${
              progress.status === 'error' 
                ? 'bg-red-50/80 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200/50 dark:border-red-800/50' 
                : progress.status === 'complete'
                ? 'bg-green-50/80 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200/50 dark:border-green-800/50'
                : 'bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-700/50 shadow-lg'
            }`}>
              <span className="text-lg">
                {progress.status === 'complete' && '✅'}
                {progress.status === 'error' && '❌'}
                {progress.status === 'processing' && (
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {!['complete', 'error', 'processing'].includes(progress.status) && '⏳'}
              </span>
              <span className="text-sm font-medium">{progress.message}</span>
            </div>
          </div>
        )}

        {/* Premium Results Section */}
        {results.length > 0 && (
          <div className="mt-16 space-y-6 animate-fade-in">
            {/* Premium Results Summary Card */}
            <div className="premium-card p-10 bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-800/90 dark:to-gray-900/50 border-gray-200/60 dark:border-gray-700/60">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
                    Results
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                    {results.length} question{results.length !== 1 ? 's' : ''} graded
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold bg-gradient-to-r from-primary via-purple-600 to-primary bg-clip-text text-transparent mb-2 tracking-tight">
                    {totalScore.toFixed(1)}/{maxPossibleScore.toFixed(1)}
                  </div>
                  <div className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                    {((totalScore / maxPossibleScore) * 100).toFixed(1)}% overall
                  </div>
                </div>
              </div>

              {/* Export Buttons */}
              <div className="mt-6 pt-6 border-t border-gray-200/60 dark:border-gray-700/60">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Export Results</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => exportToJSON({ results, totalScore, maxPossibleScore, batchName: 'Grading Results' }, 'grading-results')}
                      className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      JSON
                    </button>
                    <button
                      onClick={() => exportToCSV({ results, totalScore, maxPossibleScore, batchName: 'Grading Results' }, 'grading-results')}
                      className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      CSV
                    </button>
                    <button
                      onClick={() => exportToPDF({ results, totalScore, maxPossibleScore, batchName: 'Grading Results' }, 'grading-report')}
                      className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-primary to-purple-600 hover:from-primary-dark hover:to-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      PDF Report
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Collapsible Result Cards */}
            <div className="space-y-4">
              {results.map((result, index) => {
                const isExpanded = expandedResults.has(index);
                return (
                  <div 
                    key={result.id || index} 
                    className="premium-card overflow-hidden animate-slide-up"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    {/* Premium Result Header */}
                    <button
                      onClick={() => toggleResultExpansion(index)}
                      className="w-full p-7 text-left flex items-center justify-between hover:bg-gradient-to-r hover:from-gray-50/50 hover:to-transparent dark:hover:from-gray-800/50 transition-all duration-200 group"
                    >
                      <div className="flex items-center gap-5">
                        <div className="flex-shrink-0">
                          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 dark:from-primary/30 dark:to-purple-500/30 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-200">
                            <span className="text-primary dark:text-primary-light font-bold text-lg">Q{result.questionNumber}</span>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`text-sm font-semibold px-3 py-1 rounded-lg ${
                              result.similarityScore === 'High' || (typeof result.similarityScore === 'string' && result.similarityScore.includes('%') && parseFloat(result.similarityScore) >= 70)
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' 
                                : result.similarityScore === 'Medium' || (typeof result.similarityScore === 'string' && result.similarityScore.includes('%') && parseFloat(result.similarityScore) >= 40 && parseFloat(result.similarityScore) < 70)
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              {result.similarityScore} {result.similarityScore === 'High' || result.similarityScore === 'Medium' || result.similarityScore === 'Low' ? 'Similarity' : ''}
                            </span>
                            {result.gradedByLLM === false && (
                              <span className="text-xs px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full font-medium">
                                Text Comparison
                              </span>
                            )}
                            {result.gradedByLLM !== false && (
                              <span className="text-xs px-2.5 py-1 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 text-green-700 dark:text-green-300 rounded-full font-semibold">
                                ✨ AI Graded
                              </span>
                            )}
                          </div>
                          <div className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                            {result.value.toFixed(1)}/{result.maxScore.toFixed(1)} points
                            <span className="ml-3 text-lg font-semibold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                              ({((result.value / result.maxScore) * 100).toFixed(0)}%)
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center transition-all duration-200 group-hover:bg-gray-200 dark:group-hover:bg-gray-600 ${isExpanded ? 'rotate-180' : ''}`}>
                          <svg 
                            className="w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200"
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </button>

                    {/* Premium Result Details - Collapsible */}
                    {isExpanded && (
                      <div className="px-7 pb-7 space-y-5 border-t border-gray-200/60 dark:border-gray-700/60 pt-7 animate-fade-in">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div className="premium-card p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-900/50">
                            <div className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
                              Student Answer
                            </div>
                            <div className="p-4 bg-white/60 dark:bg-gray-800/60 rounded-xl text-sm text-gray-800 dark:text-gray-200 leading-relaxed backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
                              {result.studentAnswer || "No answer provided"}
                            </div>
                          </div>
                          <div className="premium-card p-5 bg-gradient-to-br from-green-50/50 to-emerald-50/50 dark:from-green-900/10 dark:to-emerald-900/10">
                            <div className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
                              Correct Answer
                            </div>
                            <div className="p-4 bg-white/60 dark:bg-gray-800/60 rounded-xl text-sm text-gray-800 dark:text-gray-200 leading-relaxed backdrop-blur-sm border border-green-200/50 dark:border-green-800/50">
                              {result.correctAnswer}
                            </div>
                          </div>
                        </div>

                        <div className="premium-card p-5 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10">
                          <div className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
                            Feedback
                          </div>
                          <div className="p-4 bg-white/60 dark:bg-gray-800/60 rounded-xl text-sm text-gray-800 dark:text-gray-200 leading-relaxed backdrop-blur-sm border border-blue-200/50 dark:border-blue-800/50">
                            {result.feedback}
                          </div>
                        </div>

                        {result.additionalContext && (
                          <div className="premium-card p-5 bg-gradient-to-br from-purple-50/50 to-pink-50/50 dark:from-purple-900/10 dark:to-pink-900/10">
                            <div className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
                              Additional Context
                            </div>
                            <div className="p-4 bg-white/60 dark:bg-gray-800/60 rounded-xl text-sm text-gray-800 dark:text-gray-200 leading-relaxed backdrop-blur-sm border border-purple-200/50 dark:border-purple-800/50">
                              {result.additionalContext}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Premium Empty State */}
        {results.length === 0 && !isGrading && (
          <div className="text-center py-20 animate-fade-in">
            <div className="w-24 h-24 mx-auto mb-8 text-gray-200 dark:text-gray-700">
              <div className="premium-card w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border-gray-200/60 dark:border-gray-700/60">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-12 h-12">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-3 tracking-tight">
              Ready to grade
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto text-base leading-relaxed">
              Select an input format and provide your answer key and student work to begin.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default GradingInterface;
